import { SubmitButton } from '@cibo/ui'
import { Button, FormHelperText, InputLabel, Stack } from '@mui/material'
import { Field, Form, Formik, FormikProps } from 'formik'
import { TextField } from 'formik-mui'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import {
  hasLowerCaseCheck,
  hasMinLengthCheck,
  hasNumberCheck,
  hasSymbolCheck,
  hasUpperCaseCheck,
} from '../../utils'
import PasswordRules from '../PasswordRules'
import { UserId } from '@cibo/core'

type Values = {
  password: string
  formAuthToken: string
  userId: UserId
  xMpAuthType: 'webapp'
  verifyCode: string
}

type CreatePasswordFormProps = {
  error: any
  userId: UserId
  formAuthToken: string
  isLoading: boolean
  onStartOver(): void
  onSubmit(values: Values): void
  verifyCode: any
}

const CreatePasswordForm = ({
  error,
  userId,
  formAuthToken,
  isLoading,
  onStartOver,
  onSubmit,
  verifyCode,
}: CreatePasswordFormProps) => {
  const { t } = useTranslation('auth')

  const [hasNumber, setHasNumber] = useState(false)
  const [hasSymbol, setHasSymbol] = useState(false)
  const [hasUpperCase, setHasUpperCase] = useState(false)
  const [hasLowerCase, setHasLowerCase] = useState(false)
  const [hasMinLength, setHasMinLength] = useState(false)

  /**
   * There is probably a better way to do this.
   * The validation schema never changes, but we need access to the react-i18next
   * context to localize the error messages; hence this ephemeral validation schema
   */
  const RegistrationSchema = Yup.object().shape({
    password: Yup.string()
      .required(t('passwordErrorRequired'))
      .min(8, t('passwordErrorTooShort'))
      .max(128, t('passwordErrorTooLong'))
      .test('password-quality', t('mustMeetCriteria'), (password = '') => {
        if (password === null) return false
        const hasNumber = hasNumberCheck(password)
        setHasNumber(hasNumber)
        const hasSymbol = hasSymbolCheck(password)
        setHasSymbol(hasSymbol)
        const hasLowerCase = hasLowerCaseCheck(password)
        setHasLowerCase(hasLowerCase)
        const hasUpperCase = hasUpperCaseCheck(password)
        setHasUpperCase(hasUpperCase)
        const hasMinLength = hasMinLengthCheck(password)
        setHasMinLength(hasMinLength)
        return hasNumber && hasSymbol && hasLowerCase && hasUpperCase && hasMinLength
      }),
    password2: Yup.string().test(
      'do-passwords-match',
      t('confirmPasswordErrorMismatch'),
      function (password2) {
        return this.parent.password === password2
      }
    ),
  })

  return (
    <Formik
      validationSchema={RegistrationSchema}
      initialValues={{
        formAuthToken,
        userId,
        xMpAuthType: 'webapp',
        verifyCode,
        password: '',
        password2: '',
      }}
      onSubmit={({ password2, ...rest }: Values & { password2: string }) => onSubmit(rest)}
    >
      {({ dirty, isValid }: FormikProps<Values>) => (
        <Form>
          <Stack spacing={4}>
            <PasswordRules
              hasNumber={hasNumber}
              hasSymbol={hasSymbol}
              hasUpperCase={hasUpperCase}
              hasLowerCase={hasLowerCase}
              hasMinLength={hasMinLength}
            />

            <Stack>
              <InputLabel shrink htmlFor="password">
                {t('password')}
              </InputLabel>
              <Field
                id="password"
                name="password"
                component={TextField}
                InputProps={{
                  inputProps: { autocomplete: 'new-password', 'data-testid': 'password' },
                }}
                type="password"
                disabled={isLoading}
              />
            </Stack>
            <Stack>
              <InputLabel shrink htmlFor="password2">
                {t('confirmPassword')}
              </InputLabel>
              <Field
                id="password2"
                name="password2"
                component={TextField}
                InputProps={{
                  inputProps: { autocomplete: 'new-password', 'data-testid': 'password2' },
                }}
                type="password"
                disabled={isLoading}
              />
            </Stack>

            {!!error?.message && <FormHelperText error>{error.message}</FormHelperText>}

            <Stack direction="row" justifyContent={'space-between'}>
              {onStartOver && <Button onClick={onStartOver}>{t('startOver')}</Button>}
              <SubmitButton
                disabled={isLoading || !dirty || !isValid}
                title={t('finish')}
                testId="confirm_password"
              />
            </Stack>
          </Stack>
        </Form>
      )}
    </Formik>
  )
}

export default CreatePasswordForm
